import React, {Suspense} from 'react'
import DynamicDelivery from '@components/Money/DynamicDelivery'
import RequiresRoundTrip from '@components/Money/RequiresRoundTrip'
import deliveryFeeText from '@helpers/misc/deliveryFeeText'
import useWebsiteId from '@hooks/useWebsiteId'
import {useApolloQuery as useQuery} from 'apollo-hooks'
import gql from 'graphql-tag'
import {useTranslation} from 'next-i18next'

import useSelectedPaymentMethod from '../PaymentMethod/hooks/useSelectedPaymentMethod'

import {
  CheckoutV3GetServiceFee,
  CheckoutV3GetServiceFeeVariables
} from './__generated__/CheckoutV3GetServiceFee'
import renderLine from './helpers/renderLine'

const Skeleton = () => {
  return (
    <div className="animate-pulse flex justify-between">
      <div className="w-20 h-4 bg-gray-200 rounded-lg" />
      <div className="w-12 h-4 bg-gray-200 rounded-lg" />
    </div>
  )
}

function ServiceFee() {
  const websiteId = useWebsiteId()
  const currentPaymentMethod = useSelectedPaymentMethod()
  const {data} = useQuery<CheckoutV3GetServiceFee, CheckoutV3GetServiceFeeVariables>({
    query: gql`
      query CheckoutV3GetServiceFee($websiteId: ID!) {
        userPreferences(websiteId: $websiteId) {
          _id

          address {
            _id
          }

          cart {
            deliveryType
            hasDynamicDeliveryFee
            deliveryFee
            storeId
          }

          store {
            _id
            currentExtraDeliveryCost
          }
        }
      }
    `,
    variables: {websiteId},
    fetchPolicy: 'cache-first'
  })

  const {cart, store, address} = data?.userPreferences || {}
  const {deliveryFee} = cart || {}
  const {t} = useTranslation('generic')

  if (cart?.deliveryType !== 'delivery' || !cart?.storeId) return null

  const hasDynamicFee = cart.hasDynamicDeliveryFee || !!store?.currentExtraDeliveryCost

  const deliveryContent = deliveryFeeText({address, deliveryFee})

  return renderLine(
    t('fields.dispatch'),
    <div className="flex items-center">
      <RequiresRoundTrip
        color="var(--checkout-blue)"
        hidden={!currentPaymentMethod?.requiresRoundTrip}>
        {hasDynamicFee ? (
          <DynamicDelivery value={deliveryContent} color="var(--checkout-blue)" />
        ) : (
          deliveryContent
        )}
      </RequiresRoundTrip>
    </div>
  )
}

export default function ServiceFeeWrapper() {
  return (
    <Suspense fallback={<Skeleton />}>
      <ServiceFee />
    </Suspense>
  )
}
